import React, { useState, useEffect } from "react";

import "../css/ImportContractNote.css";
import { useRefreshContext } from "../context/RefreshContext";
import Charges from "./Charges";
import {apiFetch, apiFetchJson} from "apiClient";
import { apiFetch as fetch } from "apiClient";

const ImportContractNote = ({ file, downloadedNoteId, onClose, onSuccess }) => {
    const [brokerName, setBrokerName] = useState(null);
    const [clientId, setClientId] = useState(null);
    const [contractNote, setContractNote] = useState(null);
    const [transactions, setTransactions] = useState([]);
    const [charges, setCharges] = useState([]);
    const [isDuplicateCnote, setIsDuplicateCnote] = useState(null);
    const [warning, setWarning] = useState(null);
    const [showResultsPopup, setShowResultsPopup] = useState(false);

    // Inline status banner (error/info only)
    // status = { type: 'error' | 'info', message: string }
    const [status, setStatus] = useState(null);

    const { refreshPFdata } = useRefreshContext(); // global refresh

    const showStatus = (type, message) => {
        setStatus({ type, message });
    };

    // Decide whether we are importing from local file or from a downloaded note in DB
    useEffect(() => {
        if (file) {
            handleImportFromFile();
        } else if (downloadedNoteId) {
            handleImportFromDownloadedNote();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [file, downloadedNoteId]);

    const handleImportFromFile = async () => {
        if (!file) return;

        const formData = new FormData();
        formData.append("file", file);

        try {
            // Do NOT set Content-Type for multipart; browser will add boundary
            const res = await fetch("/api/portfolio/importContractNotes", {
                method: "POST",
                body: formData,
            });
            const data = await res.json();

            if (data.error) {
                console.error("Error importing contract notes (file):", data.error);
                showStatus(
                    "error",
                    "Could not read this contract note file. Please check that it is a valid broker PDF and try again."
                );
                setShowResultsPopup(true);
                return;
            }

            populateStateFromResponse(data);
        } catch (error) {
            console.error("Error importing contract notes (file):", error);
            showStatus(
                "error",
                "Unexpected error while importing the contract note file. Please check logs and try again."
            );
            setShowResultsPopup(true);
        }
    };

    // Import using a downloaded contract note stored on server (via ID)
    const handleImportFromDownloadedNote = async () => {
        if (!downloadedNoteId) return;

        try {
            const data = await apiFetchJson(
                `/api/portfolio/importDownloadedContractNote/${downloadedNoteId}`,
                { method: "POST" }
            );

            if (data.error) {
                console.error("Error importing downloaded contract note:", data.error);
                showStatus(
                    "error",
                    "Could not load this downloaded contract note from the server. Please verify the file path and try again."
                );
                setShowResultsPopup(true);
                return;
            }

            populateStateFromResponse(data);
        } catch (error) {
            console.error("Error importing downloaded contract note:", error);
            showStatus(
                "error",
                "Unexpected error while importing this downloaded contract note. Please check logs and try again."
            );
            setShowResultsPopup(true);
        }
    };

    const populateStateFromResponse = (data) => {
        setBrokerName(data.brokerName);
        setContractNote(data.contractNote);
        setClientId(data.contractNote?.clientId);
        setTransactions(data.transactions || []);
        setCharges(data.charges || []);
        setIsDuplicateCnote(Boolean(data.isDuplicate));
        setShowResultsPopup(true);
        setWarning(data.warning);

        if (data.isDuplicate) {
            showStatus(
                "info",
                "This contract note looks like a duplicate of an already imported note. Please review before confirming."
            );
        } else {
            setStatus(null);
        }
    };

    const handleConfirmImport = async () => {
        try {
            if (!brokerName) {
                showStatus("error", "Broker name is required.");
                return;
            }

            if (!clientId || clientId.trim().length <= 1 || contractNote?.clientId === "N/A") {
                showStatus("error", "Client ID must be a valid string with more than one character.");
                return;
            }

            if (!contractNote) {
                showStatus("error", "Contract note details are missing. Please reload this note and try again.");
                return;
            }

            const updatedCharges = (Array.isArray(charges) ? charges : [charges]).map((charge) => ({
                ...charge,
                contractNoteNo: contractNote.contractNoteNo,
                settlementNo: contractNote.settlementNo,
            }));

            const payload = {
                brokerName,
                clientId,
                contractNote,
                stockTransactions: Array.isArray(transactions) ? transactions : [transactions],
                charges: updatedCharges,
                downloadedNoteId,
            };

            const res = await apiFetch("/api/portfolio/confirmImport", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });

            if (!res.ok) {
                let message = "Failed to save this contract note.";

                try {
                    const ct = res.headers.get("content-type") || "";
                    if (ct.includes("application/json")) {
                        const body = await res.json();
                        message = body?.error || body?.message || message;
                    } else {
                        const text = await res.text();
                        if (text) message = text;
                    }
                } catch (e) {
                    // ignore parse errors
                }

                showStatus("error", message);
                return;
            }

            refreshPFdata();

            if (typeof onSuccess === "function") {
                onSuccess("Contract note imported successfully. Portfolio positions and P&L will refresh shortly.");
            }

            setShowResultsPopup(false);
            if (onClose) onClose();
        } catch (error) {
            console.error("Error confirming import:", error);
            showStatus("error", error?.message || "Failed to save this contract note into the portfolio. Please try again.");
        }
    };



    const handleMarkAsImported = async () => {
        if (!downloadedNoteId) {
            showStatus(
                "error",
                "Cannot mark this as imported because it does not exist in tracking records."
            );
            return;
        }

        try {
            const res = await apiFetchJson(`/api/portfolio/${downloadedNoteId}/mark-imported`, {
                method: "POST",
            });

            refreshPFdata();

            if (typeof onSuccess === "function") {
                onSuccess("Marked as imported.");
            }

            setShowResultsPopup(false);
            if (onClose) onClose();

        } catch (error) {
            console.error("Error marking note imported:", error);
            showStatus(
                "error",
                "Failed to update entry. Try again."
            );
        }
    };

    const handleClose = () => {
        setShowResultsPopup(false);
        if (onClose) onClose();
    };

    const renderStatusBanner = () => {
        if (!status) return null;

        const { type, message } = status;
        let title = "Notice";

        if (type === "error") title = "Import failed";
        else if (type === "info") title = "Please review";

        return (
            <div className={`icn-status-banner icn-status-${type}`}>
                <div className="icn-status-text">
                    <div className="icn-status-title">{title}</div>
                    <div className="icn-status-message">{message}</div>
                </div>
                <button
                    type="button"
                    className="icn-status-close-btn"
                    onClick={() => setStatus(null)}
                    aria-label="Dismiss"
                >
                    ✕
                </button>
            </div>
        );
    };

    return (
        <>
            {showResultsPopup && (
                <div className="importcnote-overlay">
                    <div className="importcnote-popup">
                        {/* Error / info banner INSIDE popup */}
                        {renderStatusBanner()}

                        {isDuplicateCnote && (
                            <h5 style={{ color: "red", paddingBottom: "15px" }}>
                                Duplicate Contract Note
                            </h5>
                        )}
                        {warning && (
                            <h7 style={{ color: "red", paddingBottom: "15px" }}>
                                {warning || "" }
                            </h7>
                        )}
                        <div className="top-container">
                            {contractNote && (
                                <>
                                    <div className="header-row">
                                        <h3>Contract Note Details</h3>
                                    </div>

                                    <div className="header-field-container">
                                        <div className="header-row-second">
                                            <div className="form-group half-width">
                                                <label className="general-label">
                                                    Contract No:
                                                </label>
                                                <input
                                                    type="text"
                                                    value={contractNote.contractNoteNo}
                                                    onChange={(e) =>
                                                        setContractNote({
                                                            ...contractNote,
                                                            contractNoteNo: e.target.value,
                                                        })
                                                    }
                                                />
                                            </div>
                                            <div className="form-group half-width">
                                                <label className="general-label">
                                                    Broker:
                                                </label>
                                                <input
                                                    type="text"
                                                    value={brokerName || ""}
                                                    onChange={(e) =>
                                                        setBrokerName(e.target.value)
                                                    }
                                                />
                                            </div>
                                        </div>

                                        <div className="header-row-second">
                                            <div className="form-group half-width">
                                                <label className="general-label">
                                                    Trade Date:
                                                </label>
                                                <input
                                                    type="date"
                                                    value={contractNote.tradeDate}
                                                    onChange={(e) =>
                                                        setContractNote({
                                                            ...contractNote,
                                                            tradeDate: e.target.value,
                                                        })
                                                    }
                                                />
                                            </div>
                                            <div className="form-group half-width">
                                                <label className="general-label">
                                                    Settlement Date:
                                                </label>
                                                <input
                                                    type="date"
                                                    value={contractNote.settlementDate}
                                                    onChange={(e) =>
                                                        setContractNote({
                                                            ...contractNote,
                                                            settlementDate: e.target.value,
                                                        })
                                                    }
                                                />
                                            </div>
                                        </div>
                                        <div className="header-row-second">
                                            <div className="form-group half-width">
                                                <label className="general-label"></label>
                                                <label className="general-label"></label>
                                            </div>
                                            <div className="form-group half-width">
                                                <label className="general-label">
                                                    Client ID:
                                                </label>
                                                <input
                                                    type="text"
                                                    value={clientId || ""}
                                                    onChange={(e) =>
                                                        setClientId(e.target.value)
                                                    }
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}

                            {transactions.length > 0 && (
                                <div className="transaction-table-container">
                                    <h5>Transactions</h5>
                                    <div className="scrollable-table-container">
                                        <table className="import-transactions-table">
                                            <thead>
                                            <tr>
                                                <th>ISIN</th>
                                                <th>Security Name</th>
                                                <th>Type</th>
                                                <th>Quantity</th>
                                                <th>Net Obligation</th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            {transactions.map((tx, index) => (
                                                <tr key={index}>
                                                    <td>
                                                        <input
                                                            type="text"
                                                            className="transaction-input"
                                                            value={tx.isin || "N/A"}
                                                            readOnly
                                                        />
                                                    </td>
                                                    <td>
                                                        <input
                                                            type="text"
                                                            className="transaction-input"
                                                            value={tx.symbol || "N/A"}
                                                            readOnly
                                                        />
                                                    </td>
                                                    <td>
                                                        <input
                                                            type="text"
                                                            className="transaction-input"
                                                            value={tx.transactionType || "N/A"}
                                                            readOnly
                                                        />
                                                    </td>
                                                    <td>
                                                        <input
                                                            type="number"
                                                            className="transaction-input"
                                                            value={tx.quantity || "N/A"}
                                                            readOnly
                                                        />
                                                    </td>
                                                    <td>
                                                        <input
                                                            type="number"
                                                            className="transaction-input"
                                                            value={tx.netObligation || "N/A"}
                                                            readOnly
                                                        />
                                                    </td>
                                                </tr>
                                            ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {charges && Object.keys(charges).length > 0 && (
                                <div>
                                    <div className="charges-section">
                                        <Charges
                                            initialCharges={charges}
                                            onChargesUpdate={setCharges}
                                        />
                                    </div>
                                </div>
                            )}

                            <div className="contract-buttons">
                                <div className="right-buttons">
                                    {/* Import button always visible */}
                                    <button
                                        type="button"
                                        className="save-button"
                                        onClick={handleConfirmImport}
                                        disabled={!contractNote}
                                    >
                                        Import
                                    </button>

                                    {/* Show Mark-as-Imported only if duplicate */}
                                    {isDuplicateCnote && (
                                        <button
                                            type="button"
                                            className="mark-imported-btn"
                                            onClick={handleMarkAsImported}
                                            disabled={!downloadedNoteId}
                                        >
                                            Mark as Imported
                                        </button>
                                    )}

                                    <button
                                        type="button"
                                        className="cancel-button"
                                        onClick={handleClose}
                                    >
                                        Close
                                    </button>
                                </div>

                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default ImportContractNote;
